/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "gainsightpx",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing users and accounts",
          "depth": 0,
          "id": "syncing-users-and-accounts"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Tag keys",
          "depth": 1,
          "id": "tag-keys"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Delete mode",
          "depth": 1,
          "id": "delete-mode"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      img: "img",
      ul: "ul",
      li: "li",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Gainsight PX requires an API key. To find the API key, navigate to the ", _jsx(_components.a, {
          href: "https://app.aptrinsic.com/settings/api-keys",
          children: "Gainsight PX Dashboard Settings"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "In the administrative settings, select the REST API page under the An API Key can be created on this page."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gainsightpx1.png",
          alt: "The API key is in the REST API section of the dashboard"
        })
      }), "\n", _jsx(_components.p, {
        children: "If an API key has never been created, you can create one here by clicking the New API Key button."
      }), "\n", _jsx(_components.h2, {
        id: "syncing-users-and-accounts",
        children: "Syncing users and accounts"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Gainsight PX resources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Users"
        }), "\n", _jsx(_components.li, {
          children: "Accounts"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports Upsert mode."
      }), "\n", _jsx(_components.p, {
        children: "In the Upsert mode, new users or accounts will be inserted into Gainsight PX and all attributes will be kept up-to-date within Gainsight PX."
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "User records can be matched from your source to your Gainsight PX workspace by your Gainsight PX User Email in the Upsert mode."
      }), "\n", _jsx(_components.p, {
        children: "Account records can be matched from your source to your Gainsight PX workspace by your Gainsight PX Account ID in the Upsert mode."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gainsightpx2.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "tag-keys",
        children: "Tag keys"
      }), "\n", _jsx(_components.p, {
        children: "Gainsight PX requires a product to sync data to. These products can be accessed by finding the tag key of the product."
      }), "\n", _jsxs(_components.p, {
        children: ["Navigating to ", _jsx(_components.a, {
          href: "https://app.aptrinsic.com/settings/products",
          children: "Gainsight PX Dashboard Products Page"
        })]
      }), "\n", _jsx(_components.p, {
        children: "All your products can be found here."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gainsightpx4.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "The tag key can be copied and entered into the Hightouch sync configuration."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gainsightpx5.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Gainsight's default and custom fields. Hightouch automatically detects existing custom fields from your Gainsight PX account."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gainsightpx3.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "You can add custom attributes for users and accounts in your Gainsight PX account."
      }), "\n", _jsx(_components.h3, {
        id: "delete-mode",
        children: "Delete mode"
      }), "\n", _jsx(_components.p, {
        children: "You can choose what Hightouch's behavior is when records leave the query result set. The default is doing nothing, but you can also set Hightouch to delete the user or account data on record exit."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "DELETE"
          }), " : Remove a user or account record and delete the information."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
